import gql from 'graphql-tag';
import { bundle, workspaceRef, workspaceSectionRef } from './bundle';
import { business } from './business';
import { message as chatMessage, readMarker as chatReadMarker } from './chat';
import { accessLevel, short as shortUser } from './user';

// Re-export for backwards compatibility
export { workspaceRef, workspaceSectionRef };

export const report = gql`
  fragment ChannelReport on Report {
    id
    name
    source
    metabaseEmbedPath
    omniContentId
    updatedAt
    createdAt
    workflowTemplateId
  }
`;

export const group = gql`
  fragment ChannelGroup on Group {
    id
    name
    description
    users {
      ...ShortUser
    }
  }

  ${shortUser}
`;

export const membership = gql`
  fragment ChannelMembership on ChannelMembership {
    id
    readCardCount
    accessLevel {
      ...AccessLevel
    }
    user {
      id
      isMe
    }
  }

  ${accessLevel}
`;

export const details = gql`
  fragment ChannelDetails on Channel {
    id
    slug
    name
    workflowTemplateId
    imageUrl
    readAt
    description
    snoozeUntil
    type
    updatedAt
    notificationSettings
    removedUsers {
      ...ShortUser
    }
    users {
      ...ShortUser
    }
    groups {
      ...ChannelGroup
    }
    memberships: channelMemberships {
      ...ChannelMembership
    }
    visible
    unreadCount
    unreadMessagesCount: unreadCount(filter: MESSAGES)
    bundle {
      ...Bundle
    }
  }

  ${shortUser}
  ${group}
  ${membership}
  ${bundle}
`;

export const chatThread = gql`
  fragment ChannelChatThread on Channel {
    id
    slug
    name
    description
    type
    readAt
    imageUrl
    imgs
    createdAt
    business {
      ...Business
    }
    users {
      ...ShortUser
    }
    removedUsers {
      ...ShortUser
    }
    userReadMarkers {
      ...ChatUserReadMarker
    }
    isTyping {
      ...ShortUser
    }
    groups {
      ...ChannelGroup
    }

    # connection makes fetchMore easy using React Apollo (for pagination)
    messages(
      since: $since
      limit: $limit
      # If next is true (or not set), we will fetch messages _older_
      # than $since. Otherwise we will fetch newer messages. Fetching
      # newer messages ($fetchMessagesAfterSince = false) is mainly
      # used when jumping to a specific message in search.
      next: $fetchMessagesAfterSince
    ) @connection(key: "messages") {
      ...ChatMessage
    }
    visible
  }

  ${group}
  ${shortUser}
  ${chatMessage}
  ${business}
  ${chatReadMarker}
`;

export const listItem = gql`
  fragment ChannelListItem on Channel {
    id
    slug
    name
    workflowTemplateId
    type
    imageUrl
    description
    updatedAt
    deletedAt
    readAt
    unreadCount
    unreadMessagesCount: unreadCount(filter: MESSAGES)
    cardCount
    defaultFocus
    messages(limit: 1) {
      ...ChatMessage
    }
    autoAddUsers
    visible
    bundle {
      ...Bundle
    }
  }

  ${chatMessage}
  ${shortUser}
  ${bundle}
`;

export const deletedChannel = gql`
  fragment DeletedChannel on Channel {
    id
    slug
    name
    workflowTemplateId
    type
    imageUrl
    updatedAt
    deletedAt
  }
`;

export const channel = gql`
  fragment Channel on Channel {
    id
    slug
    type
    workflowTemplateId
    name
    imageUrl
    cardCount
    visible
    description
    defaultFocus
    bundle {
      ...Bundle
    }
  }

  ${bundle}
`;

export const channelCreate = gql`
  fragment ChannelCreate on Channel {
    id
    slug
    workflowTemplateId
  }
`;

export const workspaceSection = gql`
  fragment WorkspaceSection on WorkspaceSection {
    id
    title
    createdAt
    updatedAt
    channelAssociations {
      channelId
      workspaceSectionId
    }
    bundle {
      ...Bundle
    }
  }

  ${bundle}
`;

export const sharedCardLink = gql`
  fragment SharedCardLink on SharedCardLink {
    id
    type
    channelId
    userId
    cardId
    viewTemplateId
    url
    shortUrl
  }
`;
